"use client";

import { c as _c } from "react/compiler-runtime";
import type { FormEvent } from "react";
import { useContext, useEffect, useState } from "react";
import type { ApiProfileRaterCicState } from "@/entities/IProfile";
import { getStringAsNumberOrZero } from "@/helpers/Helpers";
import { AuthContext } from "@/components/auth/Auth";
import { commonApiFetch, commonApiPost } from "@/services/api/common-api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { QueryKey, ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import UserRateAdjustmentHelper from "@/components/user/utils/rate/UserRateAdjustmentHelper";
import UserPageRateInput from "@/components/user/utils/rate/UserPageRateInput";
import CircleLoader from "@/components/distribution-plan-tool/common/CircleLoader";
import { ApiProfileProxyActionType } from "@/generated/models/ApiProfileProxyActionType";
import UserPageIdentityHeaderCICRateStats from "./UserPageIdentityHeaderCICRateStats";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
const CIC_SPAN_CLASS_NAME = "tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-black/40 tw-rounded-l-lg tw-border tw-border-solid tw-border-white/[0.15] tw-px-3";
const CIC_FOCUS_RING_CLASS_NAME = "focus:tw-border-emerald-500 focus:tw-ring-1 focus:tw-ring-emerald-500/30";
const CIC_INPUT_TOOLTIP_CLASS_NAME = "tw-max-w-[12rem] -tw-ml-0.5 tw-appearance-none tw-block tw-rounded-l-none tw-rounded-r-lg tw-border tw-border-solid tw-border-white/[0.15] tw-py-3 tw-px-3 tw-bg-black/40 focus:tw-bg-black/60 tw-text-white tw-font-semibold tw-caret-emerald-400 tw-shadow-inner hover:tw-border-white/30 placeholder:tw-text-iron-500 focus:tw-outline-none tw-text-base sm:tw-text-sm tw-transition tw-duration-300 tw-ease-out";
const CIC_INPUT_FULL_CLASS_NAME = "tw-w-full -tw-ml-0.5 tw-appearance-none tw-block tw-rounded-l-none tw-rounded-r-lg tw-border tw-border-solid tw-border-white/[0.15] tw-py-3.5 tw-px-4 tw-bg-black/40 focus:tw-bg-black/60 tw-text-white tw-font-semibold tw-caret-emerald-400 tw-shadow-inner hover:tw-border-white/30 placeholder:tw-text-iron-500 focus:tw-outline-none tw-text-base sm:tw-text-sm tw-transition tw-duration-300 tw-ease-out";
export default function UserPageIdentityHeaderCICRate(t0) {
  const $ = _c(104);
  const {
    profile,
    isTooltip,
    onSuccess,
    onCancel
  } = t0;
  const {
    address
  } = useSeizeConnectContext();
  const {
    requestAuth,
    setToast,
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    onProfileCICModify
  } = useContext(ReactQueryWrapperContext);
  let t1;
  if ($[0] !== profile.handle) {
    t1 = profile?.handle?.toLowerCase();
    $[0] = profile.handle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activeProfileProxy?.created_by.handle || $[3] !== address) {
    t2 = activeProfileProxy?.created_by.handle ?? address?.toLowerCase();
    $[2] = activeProfileProxy?.created_by.handle;
    $[3] = address;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = [QueryKey.PROFILE_RATER_CIC_STATE, {
      handle: t1,
      rater: t2
    }];
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== activeProfileProxy?.created_by || $[9] !== address || $[10] !== profile.query) {
    t4 = async () => await commonApiFetch({
      endpoint: `profiles/${profile.query}/cic/rating/${activeProfileProxy?.created_by.handle ?? address?.toLowerCase()}`
    });
    $[8] = activeProfileProxy?.created_by;
    $[9] = address;
    $[10] = profile.query;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = !!address;
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      staleTime: 0
    };
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const {
    data: currentCICState
  } = useQuery(t6);
  const [mutating, setMutating] = useState(false);
  let t7;
  if ($[16] !== profile.query) {
    t7 = async amount => {
      setMutating(true);
      return await commonApiPost({
        endpoint: `profiles/${profile.query}/cic/rating`,
        body: {
          amount
        }
      });
    };
    $[16] = profile.query;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== activeProfileProxy || $[19] !== address || $[20] !== connectedProfile || $[21] !== onProfileCICModify || $[22] !== onSuccess || $[23] !== profile || $[24] !== setToast) {
    t8 = () => {
      setToast({
        message: "NIC rating updated.",
        type: "success"
      });
      onProfileCICModify({
        targetProfile: profile,
        connectedProfile: connectedProfile ?? null,
        rater: activeProfileProxy?.created_by.handle ?? address?.toLowerCase() ?? null,
        profileProxy: activeProfileProxy ?? null
      });
      onSuccess?.();
    };
    $[18] = activeProfileProxy;
    $[19] = address;
    $[20] = connectedProfile;
    $[21] = onProfileCICModify;
    $[22] = onSuccess;
    $[23] = profile;
    $[24] = setToast;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== setToast) {
    t9 = error => {
      setToast({
        message: error as unknown as string,
        type: "error"
      });
    };
    $[26] = setToast;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setMutating(false);
    };
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t11 = {
      mutationFn: t7,
      onSuccess: t8,
      onError: t9,
      onSettled: t10
    };
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const updateCICMutation = useMutation(t11);
  let t12;
  if ($[33] !== activeProfileProxy?.actions) {
    t12 = () => {
      const repProxy = activeProfileProxy?.actions.find(_temp);
      if (!repProxy) {
        return null;
      }
      return Math.max((repProxy.credit_amount ?? 0) - (repProxy.credit_spent ?? 0), 0);
    };
    $[33] = activeProfileProxy?.actions;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const getProxyAvailableCredit = t12;
  const [proxyAvailableCredit, setProxyAvailableCredit] = useState(getProxyAvailableCredit());
  let t13;
  if ($[35] !== getProxyAvailableCredit) {
    t13 = () => setProxyAvailableCredit(getProxyAvailableCredit());
    $[35] = getProxyAvailableCredit;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== activeProfileProxy) {
    t14 = [activeProfileProxy];
    $[37] = activeProfileProxy;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  useEffect(t13, t14);
  let t15;
  if ($[39] !== currentCICState?.cic_rating_by_rater || $[40] !== currentCICState?.cic_ratings_left_to_give_by_rater || $[41] !== proxyAvailableCredit) {
    t15 = () => {
      const currentCic = currentCICState?.cic_rating_by_rater ?? 0;
      const heroAvailableCic = currentCICState?.cic_ratings_left_to_give_by_rater ?? 0;
      const minHeroCic = 0 - (Math.abs(currentCic) + heroAvailableCic);
      if (typeof proxyAvailableCredit !== "number") {
        return minHeroCic;
      }
      const minProxyRep = currentCic - proxyAvailableCredit;
      return Math.abs(minHeroCic) < Math.abs(minProxyRep) ? minHeroCic : minProxyRep;
    };
    $[39] = currentCICState?.cic_rating_by_rater;
    $[40] = currentCICState?.cic_ratings_left_to_give_by_rater;
    $[41] = proxyAvailableCredit;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const getMinValue = t15;
  let t16;
  if ($[43] !== currentCICState?.cic_rating_by_rater || $[44] !== currentCICState?.cic_ratings_left_to_give_by_rater || $[45] !== proxyAvailableCredit) {
    t16 = () => {
      const currentCic_0 = currentCICState?.cic_rating_by_rater ?? 0;
      const heroAvailableCic_0 = currentCICState?.cic_ratings_left_to_give_by_rater ?? 0;
      const maxHeroCic = Math.abs(currentCic_0) + heroAvailableCic_0;
      if (typeof proxyAvailableCredit !== "number") {
        return maxHeroCic;
      }
      const maxProxyRep = currentCic_0 + proxyAvailableCredit;
      return Math.min(maxHeroCic, maxProxyRep);
    };
    $[43] = currentCICState?.cic_rating_by_rater;
    $[44] = currentCICState?.cic_ratings_left_to_give_by_rater;
    $[45] = proxyAvailableCredit;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const getMaxValue = t16;
  let t17;
  if ($[47] !== getMaxValue || $[48] !== getMinValue) {
    t17 = () => ({
      min: getMinValue(),
      max: getMaxValue()
    });
    $[47] = getMaxValue;
    $[48] = getMinValue;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  const getMinMaxValues = t17;
  let t18;
  if ($[50] !== getMinMaxValues) {
    t18 = getMinMaxValues();
    $[50] = getMinMaxValues;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  const [minMaxValues, setMinMaxValues] = useState(t18);
  let t19;
  if ($[52] !== getMinMaxValues) {
    t19 = () => setMinMaxValues(getMinMaxValues());
    $[52] = getMinMaxValues;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== currentCICState || $[55] !== proxyAvailableCredit) {
    t20 = [currentCICState, proxyAvailableCredit];
    $[54] = currentCICState;
    $[55] = proxyAvailableCredit;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  useEffect(t19, t20);
  const [originalRating, setOriginalRating] = useState(currentCICState?.cic_rating_by_rater ?? 0);
  const [adjustedRatingStr, setAdjustedRatingStr] = useState(`${originalRating}`);
  let t21;
  if ($[57] !== currentCICState?.cic_rating_by_rater) {
    t21 = () => {
      setOriginalRating(currentCICState?.cic_rating_by_rater ?? 0);
      setAdjustedRatingStr(`${currentCICState?.cic_rating_by_rater ?? 0}`);
    };
    $[57] = currentCICState?.cic_rating_by_rater;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== currentCICState) {
    t22 = [currentCICState];
    $[59] = currentCICState;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  useEffect(t21, t22);
  const newRating = getStringAsNumberOrZero(adjustedRatingStr);
  const haveChanged = newRating !== originalRating;
  const isProxy = !!activeProfileProxy;
  const isValidValue = isProxy || newRating >= minMaxValues.min && newRating <= minMaxValues.max;
  const isSaveDisabled = !haveChanged || !isValidValue;
  let t23;
  if ($[61] !== haveChanged || $[62] !== isValidValue || $[63] !== newRating || $[64] !== requestAuth || $[65] !== setToast || $[66] !== updateCICMutation) {
    const onSave = async () => {
      const {
        success
      } = await requestAuth();
      if (!success) {
        setToast({
          message: "You must be logged in.",
          type: "error"
        });
        return;
      }
      if (!haveChanged || !isValidValue) {
        return;
      }
      await updateCICMutation.mutateAsync(newRating);
    };
    t23 = async e => {
      e.preventDefault();
      await onSave();
    };
    $[61] = haveChanged;
    $[62] = isValidValue;
    $[63] = newRating;
    $[64] = requestAuth;
    $[65] = setToast;
    $[66] = updateCICMutation;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  const onSubmit = t23;
  const t24 = `tw-relative tw-flex tw-w-full ${isTooltip ? "tw-mt-1.5" : "tw-mb-2"}`;
  const t25 = isTooltip ? CIC_INPUT_TOOLTIP_CLASS_NAME : CIC_INPUT_FULL_CLASS_NAME;
  let t26;
  if ($[68] !== adjustedRatingStr || $[69] !== isProxy || $[70] !== minMaxValues || $[71] !== t25) {
    t26 = <UserPageRateInput value={adjustedRatingStr} onChange={setAdjustedRatingStr} minMax={minMaxValues} isProxy={isProxy} spanClassName={CIC_SPAN_CLASS_NAME} inputClassName={t25} inputId="nic-rating-input" focusRingClassName={CIC_FOCUS_RING_CLASS_NAME} required={true} />;
    $[68] = adjustedRatingStr;
    $[69] = isProxy;
    $[70] = minMaxValues;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== t24 || $[74] !== t26) {
    t27 = <div className={t24}>{t26}</div>;
    $[73] = t24;
    $[74] = t26;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  const rateInput = t27;
  let t28;
  if ($[76] !== mutating) {
    t28 = mutating ? <div className="tw-w-8"><CircleLoader /></div> : <>Rate</>;
    $[76] = mutating;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  const tooltipButtonContent = t28;
  let t29;
  if ($[78] !== mutating) {
    t29 = mutating ? <div className="tw-flex tw-items-center tw-justify-center"><div className="tw-w-8"><CircleLoader /></div></div> : <>Rate</>;
    $[78] = mutating;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  const fullButtonContent = t29;
  let t30;
  if ($[80] !== isTooltip || $[81] !== newRating || $[82] !== originalRating) {
    t30 = <UserRateAdjustmentHelper inLineValues={isTooltip} originalValue={originalRating} adjustedValue={newRating} adjustmentType="NIC" />;
    $[80] = isTooltip;
    $[81] = newRating;
    $[82] = originalRating;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  const adjustmentHelper = t30;
  const t31 = currentCICState?.cic_ratings_left_to_give_by_rater ?? 0;
  let t32;
  if ($[84] !== isTooltip || $[85] !== minMaxValues || $[86] !== profile || $[87] !== t31) {
    t32 = <UserPageIdentityHeaderCICRateStats isTooltip={isTooltip} profile={profile} minMaxValues={minMaxValues} heroAvailableCredit={t31} />;
    $[84] = isTooltip;
    $[85] = minMaxValues;
    $[86] = profile;
    $[87] = t31;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== adjustmentHelper || $[90] !== fullButtonContent || $[91] !== isSaveDisabled || $[92] !== isTooltip || $[93] !== onCancel || $[94] !== profile.query || $[95] !== rateInput || $[96] !== tooltipButtonContent) {
    t33 = isTooltip ? <><div className="tw-flex tw-items-end tw-gap-3"><div className="tw-w-full sm:tw-w-auto"><label htmlFor="nic-rating-input" className="tw-block tw-max-w-[12rem] tw-text-sm tw-font-normal tw-text-iron-200">Your total NIC Rating of{" "}<span className="tw-whitespace-nowrap">{profile.query}:</span></label>{rateInput}</div><div className="tw-w-full sm:tw-w-auto"><div className="tw-inline-flex tw-w-full tw-items-end tw-space-x-6 sm:tw-w-auto"><button type="submit" disabled={isSaveDisabled} className={`${!isSaveDisabled ? "hover:tw-border-emerald-400 hover:tw-bg-emerald-400" : "tw-cursor-not-allowed tw-opacity-50"} tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-emerald-500 tw-bg-emerald-500 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-emerald-500/20 tw-transition tw-duration-300 tw-ease-out sm:tw-w-auto`}>{tooltipButtonContent}</button></div></div></div>{adjustmentHelper}</> : <><label htmlFor="nic-rating-input" className="tw-mb-2 tw-block tw-text-sm tw-font-medium tw-text-iron-400">Your total NIC Rating of{" "}<span className="tw-whitespace-nowrap">{profile.query}</span></label>{rateInput}{adjustmentHelper}<div className="tw-mt-4 tw-flex tw-flex-col tw-gap-3 md:tw-flex-row-reverse"><button type="submit" disabled={isSaveDisabled} className={`${isSaveDisabled ? "tw-cursor-not-allowed tw-opacity-40" : "hover:tw-border-emerald-500 hover:tw-bg-emerald-500 active:tw-scale-[0.98]"} tw-w-full md:tw-flex-1 tw-rounded-lg tw-border tw-border-solid tw-border-emerald-600 tw-bg-emerald-600 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-emerald-500/20 tw-transition tw-duration-300 tw-ease-out`}>{fullButtonContent}</button>{onCancel && <button onClick={onCancel} type="button" className="tw-w-full md:tw-flex-1 tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800">Cancel</button>}</div></>;
    $[89] = adjustmentHelper;
    $[90] = fullButtonContent;
    $[91] = isSaveDisabled;
    $[92] = isTooltip;
    $[93] = onCancel;
    $[94] = profile.query;
    $[95] = rateInput;
    $[96] = tooltipButtonContent;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  let t34;
  if ($[98] !== onSubmit || $[99] !== t33) {
    t34 = <form onSubmit={onSubmit} className="tw-mt-6">{t33}</form>;
    $[98] = onSubmit;
    $[99] = t33;
    $[100] = t34;
  } else {
    t34 = $[100];
  }
  let t35;
  if ($[101] !== t32 || $[102] !== t34) {
    t35 = <div>{t32}{t34}</div>;
    $[101] = t32;
    $[102] = t34;
    $[103] = t35;
  } else {
    t35 = $[103];
  }
  return t35;
}
function _temp(a) {
  return a.action_type === ApiProfileProxyActionType.AllocateCic;
}